import { DATE_FORMATS, TIME_FORMATS as TF } from '@constants/date.ts';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(duration);

export const formatDateTimeBasedOnPreferences = (
    utcDateTime: string,
    dateFormat: string,
    timeFormat: string,
): string => {
    if (!utcDateTime) return '';

    const timeFormatStr = timeFormat === '12h' ? TF.twelveHours : TF.twentyFourHours;
    const fullFormat = `${dateFormat} ${timeFormatStr}`;

    return dayjs.utc(utcDateTime).local().format(fullFormat);
};

export const formatDateBasedOnPreferences = (utcDateTime: string | null, dateFormat: string): string => {
    if (!utcDateTime) return '';

    return dayjs.utc(utcDateTime).local().format(dateFormat);
};

export const isNowDaysLaterThan = (utcDateTime: string, days: number = 1): boolean => getDaysSince(utcDateTime) > days;

const getDaysSince = (pastDate: string): number => dayjs().diff(dayjs.utc(pastDate), 'day');

export interface MonthsAndDaysSince {
    months: number;
    days: number;
}

export const getMonthsAndDaysSinceString = (
    pastDate: string,
    endDateString: string | null = null,
): MonthsAndDaysSince => {
    const startDate = dayjs.utc(pastDate).local();
    const endDate = endDateString ? dayjs.utc(endDateString).local() : dayjs.utc().local();

    // Calculate total months difference
    const totalMonths = endDate.diff(startDate, 'month');
    const startPlusMonths = startDate.add(totalMonths, 'month');
    // Calculate the remaining days after full months
    const remainingDays = endDate.diff(startPlusMonths, 'day');

    return {
        months: totalMonths,
        days: remainingDays,
    };
};

export const getDaysSinceAsStringOrEmpty = (pastDate: string | null): string => {
    if (!pastDate) return '';

    return getDaysSince(pastDate).toString();
};

export const parseStringDateToISO = (val: string, dateFormat: string): string | null => {
    try {
        if (!val) return null;

        const parsed = dayjs(val);

        if (!parsed.isValid()) return null;

        const formatted = dayjs(parsed.format(dateFormat), dateFormat);

        return formatted.isValid() ? formatted.toISOString() : null;
    } catch (error) {
        console.warn('Failed to parse date:', error);
        return null;
    }
};

export const durationInMinutes = (seconds: number): string => {
    const d = dayjs.duration(seconds, 'seconds');
    const minutes = Math.floor(d.asMinutes());
    const secs = d.seconds();
    if (secs > 0) {
        return `${minutes}:${secs.toString().padStart(2, '0')} min`;
    }
    return `${minutes} min`;
};

export const secondsToWholeMinutes = (sec: number): number => Math.max(0, Math.round(sec / 60));

export const getDateFormatOptions = () =>
    Object.values(DATE_FORMATS).map((df) => ({
        label: df,
        value: df,
    }));

export const isUtcDateAfter = (firstUtc: string, secondUtc: string): boolean =>
    dayjs(firstUtc).unix() > dayjs(secondUtc).unix();
